/**
 * @file CancelAmendmentDialog.jsx
 * @module ui/src/components/tenant-admin/CancelAmendmentDialog
 * @description
 *   Confirms cancellation of a draft amendment while making the temporal consequence
 *   explicit: the approved plan remains unchanged and the cancelled draft never governs
 *   economic events. Formal-governance cancellations require an auditable reason.
 *
 * @version 1.0.0
 * @since S755
 * @author data-fit SME Build · Reviewed: Claude S755
 *
 * @architecture
 *   Locked decisions : #1, #25
 *   Temporal invariants : INV-1, INV-3
 *   Design principles : DP-2, DP-10
 *   Cross-cutting contracts : none
 *
 * @domain-rules
 *   - Cancelling an amendment does not alter the approved version.
 *   - A cancelled draft version never governs and cannot later govern economic events.
 *   - A cancellation reason is mandatory once the amendment has entered formal governance.
 *   - Cancellation is server-authoritative and must not update local navigation optimistically.
 *   - Keeping the amendment open performs no mutation.
 *
 * @session-6-migration-flags
 *   - effective_from on plan — pending m_tc_01
 *   - effective_to on plan — pending m_tc_01
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group D batch delivery
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} AmendmentRecord
 * @property {string} amendmentId
 * @property {number} draftVersion
 * @property {string} effectiveDate
 * @property {string} reason
 * @property {boolean} hasEnteredGovernance
 */

/**
 * @typedef {Object} ApprovedVersion
 * @property {number} version
 * @property {string} effectiveFrom
 * @property {string|null} effectiveTo
 */

/**
 * @typedef {Object} CancelAmendmentDialogProps
 * @property {number} planId
 * @property {AmendmentRecord} amendment
 * @property {ApprovedVersion} approvedVersion
 * @property {boolean} open
 * @property {() => void} onConfirm
 * @property {() => void} onAbort
 */

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    background: 'rgb(var(--color-bg))',
    zIndex: 1000,
    padding: '20px',
  },
  dialog: {
    width: 'min(660px, 100%)',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '22px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '8px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    fontSize: 'var(--font-sz-base)',
  },
  heading: {
    margin: 0,
    fontSize: '21px',
  },
  summary: {
    marginTop: '16px',
    padding: '14px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '6px',
    background: 'rgb(var(--color-surface-muted))',
  },
  versionBox: {
    marginTop: '14px',
    padding: '14px',
    borderLeft: '4px solid rgb(var(--color-success))',
    background: 'rgb(var(--color-surface-muted))',
  },
  field: {
    display: 'grid',
    gap: '6px',
    marginTop: '18px',
  },
  textarea: {
    width: '100%',
    minHeight: '100px',
    boxSizing: 'border-box',
    padding: '9px 10px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '5px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    font: 'inherit',
    resize: 'vertical',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '22px',
  },
};

function getFocusableElements(container) {
  // WHY: Modal focus must cycle only through active interactive controls.
  if (!container) {
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  );
}

/**
 * @param {CancelAmendmentDialogProps} props
 * @returns {React.ReactElement|null}
 */
export default function CancelAmendmentDialog({
  planId,
  amendment,
  approvedVersion,
  open,
  onConfirm,
  onAbort,
}) {
  // WHY: Cancellation reason is persisted for audit and becomes mandatory after governance entry.
  const [cancellationReason, setCancellationReason] = useState('');

  // WHY: Step is retained from the component contract, while successful completion is handed
  // immediately to the parent so local done-state rendering is not optimistic.
  const [step, setStep] = useState('confirm');

  // WHY: Pending state prevents duplicate cancellation mutations.
  const [submitting, setSubmitting] = useState(false);

  // WHY: Mutation failures remain visible in the dialog and permit retry.
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);
  const headingRef = useRef(null);

  // WHY: Opening the dialog starts a fresh cancellation decision and clears prior mutation state.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setCancellationReason('');
    setStep('confirm');
    setSubmitting(false);
    setError(null);

    const focusTimer = window.setTimeout(() => {
      headingRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [open, amendment.amendmentId]);

  // WHY: Cancellation is modal governance interaction, so keyboard focus remains contained
  // and Escape preserves the amendment by invoking onAbort.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      // WHY: Escape means abandon cancellation and keep the amendment open.
      if (event.key === 'Escape') {
        event.preventDefault();
        onAbort();
        return;
      }

      // WHY: Only Tab navigation is intercepted for focus containment.
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(dialogRef.current);

      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // WHY: Reverse tabbing wraps within the cancellation modal.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }

      // WHY: Forward tabbing wraps within the cancellation modal.
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onAbort]);

  // WHY: Governance entry changes the audit requirement; submitted amendments need an explicit reason.
  const reasonRequired = amendment.hasEnteredGovernance;

  const reasonValid =
    !reasonRequired || cancellationReason.trim().length > 0;

  // WHY: Reason edits change only local confirmation data and clear stale request failures.
  const handleReasonChange = (event) => {
    setCancellationReason(event.target.value);
    setError(null);
  };

  // WHY: Keeping the amendment open is explicitly a no-mutation branch.
  const handleKeepOpen = () => {
    if (!submitting) {
      onAbort();
    }
  };

  // WHY: Cancellation is authoritative only after the server confirms it. The approved
  // version is not altered locally, and onConfirm is deferred until success.
  const handleConfirmCancellation = async () => {
    if (submitting || !reasonValid) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // WHY: Required planId identifies the parent plan for the amendment cancellation endpoint.
      const response = await fetch(
        `/api/v1/plans/${planId}/amendments/${amendment.amendmentId}/cancel`,
        {
          method: 'POST',
          credentials: 'include',  // OI-S900: was missing — 401 in production
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            cancellationReason: cancellationReason.trim(),
          }),
        },
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(
          payload?.detail ||
            payload?.message ||
            'The amendment could not be cancelled.',
        );
      }

      // WHY: This local marker is set only after server confirmation and is immediately
      // followed by the parent success callback; it is not an optimistic domain transition.
      setStep('done');
      onConfirm();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'The amendment could not be cancelled.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // WHY: Closed controlled dialogs must not remain focusable or expose stale cancellation state.
  if (!open) {
    return null;
  }

  // WHY: Parent normally closes on successful onConfirm; this server-confirmed fallback
  // prevents duplicate controls if parent state propagation is not synchronous.
  if (step === 'done') {
    return (
      <div style={styles.backdrop}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-amendment-title"
          tabIndex={-1}
          style={styles.dialog}
        >
          <h2
            id="cancel-amendment-title"
            ref={headingRef}
            tabIndex={-1}
            style={styles.heading}
          >
            Amendment cancelled
          </h2>
          <p className="modifier-manager-success">
            The server confirmed the amendment cancellation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.backdrop}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-amendment-title"
        tabIndex={-1}
        style={styles.dialog}
      >
        <h2
          id="cancel-amendment-title"
          ref={headingRef}
          tabIndex={-1}
          style={styles.heading}
        >
          Cancel amendment
        </h2>

        <div style={styles.summary}>
          <p>
            <strong>Draft version:</strong> V{amendment.draftVersion}
          </p>
          <p>
            <strong>Proposed economic effective date:</strong>{' '}
            {amendment.effectiveDate}
          </p>
          <p>
            <strong>Original amendment reason:</strong> {amendment.reason}
          </p>
        </div>

        <div style={styles.versionBox}>
          <strong>
            Approved version remaining applicable: V{approvedVersion.version}
          </strong>
          <p>
            Effective window: {approvedVersion.effectiveFrom} through{' '}
            {approvedVersion.effectiveTo || 'open-ended'}
          </p>
        </div>

        {/* WHY: Locked copy gives the administrator explicit assurance that cancelling
            an ungoverned draft cannot alter production compensation. */}
        <p>
          Cancelling this amendment will not affect the approved plan. V
          {approvedVersion.version} will continue to govern as currently applicable.
        </p>
        <p>
          The proposed V{amendment.draftVersion} has never governed and will
          never govern economic events.
        </p>

        <label style={styles.field}>
          <span>
            Cancellation reason
            {reasonRequired ? ' (required)' : ' (optional)'}
          </span>
          <textarea
            style={styles.textarea}
            value={cancellationReason}
            onChange={handleReasonChange}
            required={reasonRequired}
          />
        </label>

        {/* WHY: API failure remains a cancellation error and never changes the displayed
            approved-version applicability state. */}
        {error ? (
          <p
            className="modifier-manager-inline-error"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <div style={styles.actions}>
          <button
            type="button"
            className="secondary-button"
            disabled={submitting}
            onClick={handleKeepOpen}
          >
            Keep amendment open
          </button>

          <button
            type="button"
            className="primary-button"
            disabled={submitting || !reasonValid}
            onClick={handleConfirmCancellation}
          >
            {submitting ? 'Cancelling…' : 'Confirm cancellation'}
          </button>
        </div>
      </div>
    </div>
  );
}

CancelAmendmentDialog.propTypes = {
  planId: PropTypes.number.isRequired,
  amendment: PropTypes.shape({
    amendmentId: PropTypes.string.isRequired,
    draftVersion: PropTypes.number.isRequired,
    effectiveDate: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
    hasEnteredGovernance: PropTypes.bool.isRequired,
  }).isRequired,
  approvedVersion: PropTypes.shape({
    version: PropTypes.number.isRequired,
    effectiveFrom: PropTypes.string.isRequired,
    effectiveTo: PropTypes.string,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onAbort: PropTypes.func.isRequired,
};

/**
 * @example
 * <CancelAmendmentDialog
 *   planId={418}
 *   amendment={{
 *     amendmentId: '632c94a8-43a8-4ee5-905b-a2f6764d655f',
 *     draftVersion: 4,
 *     effectiveDate: '2026-08-15',
 *     reason: 'Accelerator redesign',
 *     hasEnteredGovernance: true,
 *   }}
 *   approvedVersion={{
 *     version: 3,
 *     effectiveFrom: '2026-01-01',
 *     effectiveTo: null,
 *   }}
 *   open={cancelDialogOpen}
 *   onConfirm={() => refreshPlan()}
 *   onAbort={() => setCancelDialogOpen(false)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - planId is a required prop and supplies the parent-plan endpoint segment.
 * - Approved version is explicitly described as unaffected by cancellation.
 * - Draft version is explicitly described as never having governed.
 * - Governance-entered amendments require a cancellation reason.
 * - Keep amendment open performs no API mutation.
 * - onConfirm executes only after server-confirmed cancellation.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - effective_from on plan — pending m_tc_01
 * - effective_to on plan — pending m_tc_01
 */

/**
 * ACCESSIBILITY NOTES
 * - Dialog has modal semantics and an accessible heading.
 * - Heading receives focus when the dialog opens.
 * - Tab navigation is trapped within the modal.
 * - Escape invokes onAbort, preserving the amendment.
 * - Required cancellation reason is conveyed in both visible copy and the native required attribute.
 * - Server failures use role="alert".
 */
